// "Possible relations" — the emails Busy thinks belong to one of the user's Ongoings, offered in
// the Email Briefing (Figma 40009470-209128; coordinator's brief, 2026-09-02).
//
// The offers themselves are not new: a Suggested Contains→Timeline edge is what home's review list
// asked about until busy-my-web#295, and what the Ongoing's own "Connect this email to this
// Ongoing" block (busy-my-web#291) still asks. What was missing is a place to see them ALL, next to
// the mail: home was told to carry no per-thread questions, and the story block only shows the
// offers of the story you are in. So the Briefing gets a banner — "3 emails may belong to an
// Ongoing" — that opens into the rows, and home gets one line that leads there.
//
// Same edge, same two verbs. The rows are read from `ongoing/suggestions` (the list the store
// already polls), narrowed to the thread offers by lib/storyAutomation.js's own rule, and answered
// with the store's `addSuggestedThread` / `rejectSuggestedThread` — so an offer answered here is
// answered in the story block too, and the rejection is kept so the pair is never offered again.
//
// Pure — no store, no api, nothing mounted.

import { connectionSuggestions, ongoingRoute, signalsLine, threadSuggestions } from '@/lib/storyAutomation'
import { threadPath } from '@/lib/threadOngoing'

export const TEXT = {
  heading: 'Possible relations',
  review: 'Review',
  hide: 'Hide',
  openThread: 'Open thread',
  add: 'Add',
  notRelated: 'Not related',
  viewOngoing: 'View Ongoing',
}

/** Where the banner lives, and where home's line goes — opened on its rows (`?relations=1`). */
export const RELATIONS_ROUTE = '/emails/workspace?relations=1'
export const RELATIONS_SECTION = 'email-relations'

/**
 * One offer as the banner draws it.
 *
 * The label is "<sender> · <subject>" — the same line the story block uses (lib/storyOffers.js),
 * because a person recognises an email by who sent it before what it says. Either half alone still
 * makes a line; an offer with neither is dropped rather than drawn empty. The sentence under it
 * says WHICH Ongoing and WHY, in the server's own words ("mentions 'build', which 'iphone 17
 * promax' is researching"), with the signal names as a quieter third line where a reason is absent.
 */
export function relationOffer(item) {
  const relationshipId = String(item?.relationshipId || '').trim()
  if (!relationshipId) return null
  const sender = String(item?.from || item?.sender || '').trim()
  const subject = String(item?.subject || '').trim()
  const label = [sender, subject].filter(Boolean).join(' · ')
  if (!label) return null
  const story = String(item?.ongoingTitle || item?.title || '').trim()
  const reason = String(item?.reason || '').trim()
  // WHAT the offered thing is (Pouria, 2026-10-01, frame 40011046:161537): an entity offer
  // (busy-timeline-srv#42) names its `module` — task / note / event / researchpack — and the
  // story's connections table draws each row in that module's glyph and colour. Dropping it here
  // is what drew every row as an envelope. A thread offer is an email whatever it says.
  const kind = String(item?.kind || '').trim()
  const module = kind === 'thread' ? 'email' : (String(item?.module || '').trim().toLowerCase() || 'email')
  const isEmail = module === 'email'
  return {
    id: relationshipId,
    relationshipId,
    timelineId: String(item?.timelineId || '').trim() || null,
    ongoingId: item?.ongoingId ? String(item.ongoingId) : null,
    sender,
    subject,
    label,
    story,
    // "May belong to Kitchen renovation — mentions 'tiles'…": the story first, then the why.
    why: [story ? `May belong to ${story}` : 'May belong to one of your Ongoings', reason].filter(Boolean).join(' — '),
    reason,
    signals: signalsLine(item?.signals),
    confidence: typeof item?.confidence === 'number' ? item.confidence : null,
    // WHEN the offered thread was last touched (`receivedUtc`, the timeline's update date — see
    // TimelineSrv's OngoingSuggestionResponse). Carried raw, not formatted: the match cards
    // (busy-my-web#317) say "updated 11 Mar" from the newest of several offers, so the comparison
    // has to happen on the date rather than on its wording.
    received: item?.receivedUtc || item?.received || null,
    // …and the rest of what an entity offer carries, raw, for the connections table's review card:
    // the kind and module above, the entity's own id and ref (what opening it needs), the server's
    // `facts` (created / updated / due / home Ongoing), its reason LIST and its own confidence band.
    kind,
    module,
    entityId: item?.entityId ? String(item.entityId) : null,
    entityRef: item?.entityRef || null,
    facts: item?.facts || null,
    reasons: Array.isArray(item?.reasons) ? item.reasons.filter(Boolean).map(String) : [],
    band: String(item?.band || '').trim().toLowerCase() || null,
    // The graph node's own SUMMARY (busy-graph-srv enriches every node, ≤ 400 chars; the BFF's
    // Graph:Enabled mapping passes it as `summary` on thread and entity rows — busy-my-web#710).
    // The possible-connection view card's Summary mode reads it; '' when the node has none yet.
    summary: String(item?.summary || '').trim(),
    // Only an EMAIL has a thread to read: an entity offer carries a `timelineId` too (the task's or
    // note's own timeline), and routing that into the mail reader would open a blank conversation.
    threadRoute: isEmail ? (threadPath(item?.timelineId) || null) : null,
    viewRoute: ongoingRoute(item?.ongoingId),
  }
}

/** Every offer the list holds, junk dropped, in the order the server ranked them (newest first). */
export function relationOffers(items) {
  return threadSuggestions(items).map(relationOffer).filter(Boolean)
}

// ── NOT ANOTHER ONGOING (Saffet, 2026-10-21) ────────────────────────────────────────────────────
//
// "I created a Which no credit check SIM plan has the best UK 5G coverage n ongoing and created 2
// research packs but when I click 4 possible connections for my Ongoing it shows me other Ongoings
// to connect to. And nothing to do with the Ongoing and also it should try and match to an email or
// something that I sent attached not another ongoing."
//
// Two kinds of offer were reaching the story's table that are about OTHER stories rather than about
// things that may belong to this one: `module: "ongoing"` rows (busy-timeline-srv#49, a whole other
// story offered as related) and `module: "researchpack"` rows (busy-timeline-srv#42, a pack that
// lives in another story). Saffet's four were exactly those — "Best hoover for the office", "Is the
// dyson any good?" — matched on "Researched in the same period" and a shared common word. The
// table now lists the user's own emails, tasks, notes, events and contacts only. The service still
// sends the other two; filtering them out of THIS list (the only reader of `connectionOffers`)
// keeps the Briefing's count and the table's rows the same number.
const OTHER_ONGOING_MODULES = new Set([
  'ongoing', 'ongoings', 'story', 'stories',
  'researchpack', 'researchpacks', 'research-pack', 'research-packs', 'research_pack',
])

/** Is this offer about ANOTHER Ongoing (a related story, or a pack that lives in one)? */
export function aboutAnotherOngoing(offer) {
  return OTHER_ONGOING_MODULES.has(String(offer?.module || '').trim().toLowerCase())
}

/**
 * Every POSSIBLE CONNECTION the list holds — threads AND entities (Pouria, 2026-10-01, frame
 * 40011046:161537). The story's connections table and its Briefing row read this; everything that
 * is about MAIL (the workspace cards, home's line, the banner) keeps `relationOffers` above.
 */
export function connectionOffers(items) {
  return connectionSuggestions(items).map(relationOffer).filter(Boolean).filter(o => !aboutAnotherOngoing(o))
}

/** "3 emails may belong to an Ongoing" — the banner's line, and home's. '' for none. */
export function relationsLabel(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  return `${n} email${n === 1 ? '' : 's'} may belong to an Ongoing`
}

/**
 * Home's line (the coordinator's "alert that redirects to possible relations"): one row, ranked
 * right after the Briefing row, that opens the workspace where the banner is. None without offers —
 * a notice about nothing is noise.
 */
export function relationsFollowUpRow(items) {
  const n = relationOffers(items).length
  if (!n) return null
  return {
    section: RELATIONS_SECTION,
    label: `${TEXT.heading}: ${relationsLabel(n)}`,
    count: n,
    rank: 1,
    kind: 'email',
    route: RELATIONS_ROUTE,
  }
}

/** The rows minus one answered id — what the banner shows the moment Add / Not related is clicked. */
export function withoutRelation(rows, relationshipId) {
  const id = String(relationshipId || '')
  return (rows || []).filter(r => r && String(r.id) !== id)
}

export default relationOffers
